import { Trans } from "@lingui/macro";
import { FC } from "react";
import Link from "next/link";
import {
  Wrapper,
  Header,
  Content,
  DescriptionContent,
  BrandName,
  ProductName,
  SizeContent,
  PriceContent,
  Image,
  Footer,
} from "./AddedToCart.styled";
import AddedToCartSuggestions from "./AddedToCartSuggestions";
import { Button } from "src/atoms/Button";
import { Price } from "src/atoms/Price";
import { BaseMoney, Money } from "src/types/ctgraphql.d";
import { AddedToCartProduct } from "src/types/addedToCardProduct.d";
import { Heading, Text, AlertBanner } from "@otrium/atoms";
import { CloseButton } from "src/atoms/CloseButton";
import { Box } from "@otrium/core";
import { CTPrice } from "src/atoms/CTPrice";
import { PromotionDisplayType } from "src/types/graphql.d";
import { Flash } from "src/icons/Flash";

interface Props extends AddedToCartProduct {
  onClose: () => void;
  onInteraction: () => void;
  cartMinLineItemsTotalPriceDiff: Money | null;
  cartTotal?: BaseMoney | null;
  showAddedProduct?: boolean;
}

const AddedToCartContent: FC<Props> = ({
  productId,
  productName,
  productSlug,
  brandName,
  price,
  regularPrice,
  image,
  selectedSize,
  cartMinLineItemsTotalPriceDiff,
  cartTotal,
  onClose,
  onInteraction,
  showAddedProduct = false,
  promoCode,
  promotionDisplayType,
}) => {
  return (
    <Wrapper
      data-testid="add-to-cart-popup"
      onMouseMove={onInteraction}
      onTouchStart={onInteraction}
      onKeyDown={onInteraction}
    >
      <Header>
        <Heading
          as="h4"
          fontFamily="PlusJakartaSans"
          fontSize={["fontSize18", "fontSize18", "fontSize20"]}
          fontWeight={700}
          lineHeight="24px"
          color="tone.black"
        >
          <Trans>Added to bag</Trans>
        </Heading>
        <CloseButton
          onClick={onClose}
          iconSize={24}
          sx={{
            height: 24,
            padding: 0,
            width: 24,
          }}
        />
      </Header>

      {showAddedProduct && (
        <Content>
          {image && <Image src={image} alt={`${brandName} ${productName}`} />}
          <DescriptionContent>
            <BrandName data-testid="add-to-cart-popup-brandName">
              {brandName}
            </BrandName>
            <ProductName data-testid="add-to-cart-popup-productName">
              {productName}
            </ProductName>
            <SizeContent>
              <span>
                <Trans>Size</Trans>
              </span>
              {selectedSize}
            </SizeContent>
            <PriceContent data-testid="add-to-cart-popup-price">
              <Price price={price} />
              <Text as="span" color="tone.anthracite">
                <Price price={regularPrice} />
                {"*"}
              </Text>
              {promoCode &&
              promotionDisplayType === PromotionDisplayType.LightningDeal ? (
                <span className="promo-code">
                  <Flash />
                  {promoCode}
                </span>
              ) : null}
            </PriceContent>
          </DescriptionContent>
        </Content>
      )}

      <AddedToCartSuggestions productId={productId} productSlug={productSlug} />

      {cartMinLineItemsTotalPriceDiff &&
        cartMinLineItemsTotalPriceDiff.centAmount > 0 && (
          <Box mt={3}>
            <AlertBanner variant="info">
              <Text variant="body.source.semibold14">
                <Trans>
                  Almost there! Add{" "}
                  <CTPrice price={cartMinLineItemsTotalPriceDiff} /> to your
                  order to unlock check-out.
                </Trans>
              </Text>
            </AlertBanner>
          </Box>
        )}

      {cartTotal && (
        <Footer>
          <Link href="/cart" passHref legacyBehavior>
            <Button
              as="a"
              variant="primary"
              data-testid="order-now-button"
              fullWidth
            >
              <CTPrice price={cartTotal} />
              &nbsp;-&nbsp;<Trans>Checkout</Trans>
            </Button>
          </Link>
        </Footer>
      )}
    </Wrapper>
  );
};

export default AddedToCartContent;
